// Auto-extracted FAQ seed. Edit via Admin → Fact checker → FAQs.
export type FaqPage = "beaches" | "trails" | "dining" | "nightlife" | "surf" | "tides" | "weather" | "activities" | "news" | "events";

export interface FaqItem {
  id: string;
  page: FaqPage;
  question: string;
  answer: string;
  sort_order: number;
}

export const FAQS_SEED: FaqItem[] = [
  {
    "id": "beaches-faq-1",
    "page": "beaches",
    "question": "What is the best beach in San Diego for first-time visitors?",
    "answer": "La Jolla Shores is the easiest first impression: a wide flat beach, gentle surf, lifeguards, paid parking nearby, and tide pools at low tide. Coronado is a strong second — wider sand, the Hotel del Coronado backdrop, and a long flat stretch perfect for walking.",
    "sort_order": 0
  },
  {
    "id": "beaches-faq-2",
    "page": "beaches",
    "question": "Can you swim at every San Diego beach?",
    "answer": "Most beaches are swimmable in summer, but conditions vary daily. Check the lifeguard flag system before getting in. Avoid swimming within 72 hours of heavy rain — runoff brings bacteria from inland watersheds. Sunset Cliffs and Point Loma are scenic but not safe swimming beaches due to rocks and surge.",
    "sort_order": 1
  },
  {
    "id": "beaches-faq-3",
    "page": "beaches",
    "question": "Where can I see tide pools in San Diego?",
    "answer": "The best tide pools are at Cabrillo National Monument (Point Loma), La Jolla Shores at the south end near the cliffs, and Sunset Cliffs at low tide. Always check the tide chart and aim for a tide of 0.0 ft or lower. Look but don't touch — most marine life is protected.",
    "sort_order": 2
  },
  {
    "id": "beaches-faq-4",
    "page": "beaches",
    "question": "Are San Diego beaches dog friendly?",
    "answer": "Dog Beach in Ocean Beach is the original off-leash beach and open 24 hours. Coronado has a leash-free section at the north end. Most other beaches allow leashed dogs only outside the 9am–6pm window in summer. Always check posted signs at the entrance.",
    "sort_order": 3
  },
  {
    "id": "beaches-faq-5",
    "page": "beaches",
    "question": "When is the best time of year to visit San Diego beaches?",
    "answer": "September and October offer the warmest water (around 68–70°F), thinner crowds after Labor Day, and the most reliable sun. June can be cloudy due to 'June Gloom' marine layer. Winter beaches are spectacular for walks and storm-watching but cool for swimming.",
    "sort_order": 4
  },
  {
    "id": "beaches-faq-6",
    "page": "beaches",
    "question": "Do San Diego beaches charge for parking?",
    "answer": "Most coastal lots are free but fill by 9–10am in summer. Paid lots near La Jolla Cove, Mission Beach, and Coronado run $2–4/hour. Street parking is metered in busy zones. Arrive early or use the MTS coastal bus routes for stress-free access.",
    "sort_order": 5
  },
  {
    "id": "trails-faq-1",
    "page": "trails",
    "question": "What is the most popular hike in San Diego?",
    "answer": "Torrey Pines State Natural Reserve is the iconic coastal hike — a network of moderate trails through the only remaining wild Torrey pine forest in the world, with cliffs over the Pacific. The Razor Point and Beach Trail loop is about 1.5 miles round trip and one of the most photographed walks in California.",
    "sort_order": 6
  },
  {
    "id": "trails-faq-2",
    "page": "trails",
    "question": "What is the best time of year to hike in San Diego?",
    "answer": "October through May. Cooler temperatures, occasional wildflowers in February–March after winter rains, and clearer ocean visibility. Summer hikes are still doable but start at sunrise — by 11am inland trails can hit 90°F+ with little shade.",
    "sort_order": 7
  },
  {
    "id": "trails-faq-3",
    "page": "trails",
    "question": "Are there easy trails for kids and beginners?",
    "answer": "Yes. The Coast Walk Trail in La Jolla (mostly flat, ocean views), the Beach Trail at Torrey Pines (short, paved sections), and the boardwalk at Cabrillo National Monument are all suitable for young children and casual walkers.",
    "sort_order": 8
  },
  {
    "id": "trails-faq-4",
    "page": "trails",
    "question": "Do I need a permit to hike in San Diego?",
    "answer": "Most trails are free. Torrey Pines State Reserve and Cabrillo National Monument charge a parking fee ($15–25/vehicle, or use an annual pass). Cuyamaca and Anza-Borrego state parks have day-use fees. Trail itself is always free; the parking lot is what costs.",
    "sort_order": 9
  },
  {
    "id": "trails-faq-5",
    "page": "trails",
    "question": "Are there any rattlesnakes on the trails?",
    "answer": "Yes — Western rattlesnakes are common in coastal sage scrub year-round, more active March through October. Stay on marked trails, don't reach into rocks or bushes, and watch where you step. Bites are rare with awareness. Keep dogs on leash.",
    "sort_order": 10
  },
  {
    "id": "trails-faq-6",
    "page": "trails",
    "question": "Where can I find wildflowers?",
    "answer": "After a wet winter, the Anza-Borrego desert (1.5 hrs east) blooms spectacularly in late February and March. Closer in, Mission Trails Regional Park, Torrey Pines Reserve, and the Bayside Trail at Cabrillo all show good native wildflower displays in spring.",
    "sort_order": 11
  },
  {
    "id": "dining-faq-1",
    "page": "dining",
    "question": "What food is San Diego known for?",
    "answer": "Fish tacos, California burritos, and craft beer top the list. The fish taco — battered or grilled white fish, cabbage, crema, on a corn tortilla — was popularized here in the 1980s. The California burrito (carne asada, fries, cheese, guac) is a true San Diego invention. The city also has one of the densest craft brewery scenes in America.",
    "sort_order": 18
  },
  {
    "id": "dining-faq-2",
    "page": "dining",
    "question": "Where can I find the best fish tacos?",
    "answer": "Oscar's Mexican Seafood, City Tacos, and Mariscos German truck are local favorites. Tourist-area taco shops are usually fine but rarely the best. The original-style fish tacos came from Baja and arrived in San Diego in the 1980s — for the closest version to the source, head to Old Town or the Convoy district.",
    "sort_order": 19
  },
  {
    "id": "dining-faq-3",
    "page": "dining",
    "question": "Is San Diego dining expensive?",
    "answer": "It runs the full range. A great fish taco can cost $4–6; a beachfront tasting menu can hit $200/person. La Jolla, Little Italy, and Coronado lean upscale. Barrio Logan, City Heights, and Convoy Asian District offer outstanding meals under $20. Most neighborhoods have both extremes within a few blocks.",
    "sort_order": 20
  },
  {
    "id": "dining-faq-4",
    "page": "dining",
    "question": "Do I need reservations for popular restaurants?",
    "answer": "For oceanfront tables at sunset, yes — book 2–4 weeks ahead, especially on weekends. Mid-tier and casual spots usually accept walk-ins. Use OpenTable, Resy, or call directly. Some popular places (Born & Raised, Herb & Wood, Addison) require reservations weeks in advance.",
    "sort_order": 21
  },
  {
    "id": "dining-faq-5",
    "page": "dining",
    "question": "Where can I eat with a real ocean view?",
    "answer": "Top of the Cove and George's at the Cove (La Jolla), The Marine Room (La Jolla Shores — waves literally hit the windows), Coasterra (Harbor Island), and the rooftop at Hotel del Coronado are the classic ocean-view dining rooms in San Diego.",
    "sort_order": 22
  },
  {
    "id": "dining-faq-6",
    "page": "dining",
    "question": "Is San Diego good for vegan and vegetarian dining?",
    "answer": "Increasingly, yes. Plumeria (University Heights), Donna Jean (Bankers Hill), Trilogy Sanctuary (La Jolla — vegan rooftop yoga café), and Civico 1845 (Little Italy) all have full vegan menus or robust vegetarian offerings. Most modern San Diego restaurants offer multiple plant-based options.",
    "sort_order": 23
  },
  {
    "id": "nightlife-faq-1",
    "page": "nightlife",
    "question": "What is the best nightlife neighborhood in San Diego?",
    "answer": "The Gaslamp Quarter is the densest and most tourist-friendly — clubs, rooftop bars, and live music within walking distance. North Park and South Park are where locals go for craft cocktails and dive bars. Pacific Beach is loud, beachy, and youth-driven. Little Italy mixes wine bars and upscale lounges.",
    "sort_order": 30
  },
  {
    "id": "nightlife-faq-2",
    "page": "nightlife",
    "question": "How late do San Diego bars stay open?",
    "answer": "Last call in California is 1:30am, with bars required to close by 2am. Some venues stop serving earlier. Weekend nights in the Gaslamp routinely stay packed until 1:45am. Brewery taprooms often close earlier — many wrap up by 10 or 11pm.",
    "sort_order": 31
  },
  {
    "id": "nightlife-faq-3",
    "page": "nightlife",
    "question": "Is there an Uber/Lyft scene in San Diego?",
    "answer": "Yes — both ride-share apps are reliable across the city, with surge pricing on weekend nights, especially after 12:30am near the Gaslamp. The MTS Trolley runs late on Friday and Saturday nights. Driving and parking downtown on a Saturday is generally not worth the hassle.",
    "sort_order": 32
  },
  {
    "id": "nightlife-faq-4",
    "page": "nightlife",
    "question": "What's the dress code at San Diego clubs?",
    "answer": "Gaslamp clubs typically enforce 'dressy casual' — no athletic wear, no flip-flops, no shorts for men at most upscale spots. Beach-area bars in PB and OB are intentionally casual — swimsuit cover-ups are normal. North Park is hip but unfussy.",
    "sort_order": 33
  },
  {
    "id": "nightlife-faq-5",
    "page": "nightlife",
    "question": "Are there good live music venues?",
    "answer": "Yes — Belly Up Tavern (Solana Beach) is legendary for mid-size touring acts. House of Blues and the Observatory North Park host bigger national bills. Casbah is the storied indie/punk venue. Humphreys by the Bay does intimate outdoor concerts in summer.",
    "sort_order": 34
  },
  {
    "id": "nightlife-faq-6",
    "page": "nightlife",
    "question": "Can I have a beach bonfire at night?",
    "answer": "Yes, in designated fire pits at Mission Beach, Ocean Beach, La Jolla Shores, Coronado, and Mariners Point. They're first-come, first-served and fill up by 4–5pm in summer. Fires must be in the rings, fully extinguished by 10–11pm depending on beach, and pack out all trash.",
    "sort_order": 35
  },
  {
    "id": "surf-faq-1",
    "page": "surf",
    "question": "Where should beginners surf in San Diego?",
    "answer": "Pacific Beach (especially the south end near Tourmaline Surfing Park) and San Onofre to the north are the most beginner-friendly. Both have soft, slow-breaking waves, sandy bottoms, and surf schools on the beach. Avoid La Jolla reefs and Sunset Cliffs until you're confident on a longboard.",
    "sort_order": 42
  },
  {
    "id": "surf-faq-2",
    "page": "surf",
    "question": "What is the best time of year to surf in San Diego?",
    "answer": "Winter (November–March) brings the largest, most consistent NW swells from North Pacific storms — best for experienced surfers. Summer (May–August) gets smaller, mellower S/SW swells from the Southern Hemisphere, perfect for beginners and longboarders. Spring and fall mix both.",
    "sort_order": 43
  },
  {
    "id": "surf-faq-3",
    "page": "surf",
    "question": "How cold is the water for surfing?",
    "answer": "Water averages 58°F in winter and up to 70°F in late summer. Most locals wear a 3/2 mm full suit October through May, and either a spring suit or boardshorts in July, August, and September. A 4/3 mm suit will cover you year-round if you only own one.",
    "sort_order": 44
  },
  {
    "id": "surf-faq-4",
    "page": "surf",
    "question": "Do I need to know surf etiquette before paddling out?",
    "answer": "Yes — every break has unwritten rules. The basics: the surfer closest to the breaking peak has priority, don't drop in on someone already riding, paddle wide around the lineup, and never snake. San Diego lineups can be territorial, especially Windansea and Big Rock — start at less crowded breaks.",
    "sort_order": 45
  },
  {
    "id": "surf-faq-5",
    "page": "surf",
    "question": "Where can I rent a surfboard in San Diego?",
    "answer": "Mission Beach, Pacific Beach, and La Jolla Shores all have multiple shops with daily and weekly rentals. Soft-top longboards run $25–40/day. Most shops also rent wetsuits. If you'll be here a week, ask about discounted multi-day rates.",
    "sort_order": 46
  },
  {
    "id": "surf-faq-6",
    "page": "surf",
    "question": "What's the difference between a beach break and a reef break?",
    "answer": "Beach breaks (Pacific, Mission, Imperial) form over sandbars — the wave shape changes daily and the bottom is forgiving. Reef breaks (La Jolla, Sunset Cliffs) form over rock or coral — they break more predictably and powerfully but a fall can mean injury. Beginners stick to beach breaks.",
    "sort_order": 47
  },
  {
    "id": "tides-faq-1",
    "page": "tides",
    "question": "How do I read a tide chart?",
    "answer": "A tide chart shows water height (in feet) over time. Two highs and two lows happen daily on the San Diego coast. Numbers above zero mean water is higher than the long-term average; negative numbers (e.g. -0.6 ft) mean an exceptionally low tide — perfect for tide pooling. The tide moves in roughly six-hour cycles between extremes.",
    "sort_order": 54
  },
  {
    "id": "tides-faq-2",
    "page": "tides",
    "question": "What is the best tide for tide pooling?",
    "answer": "Aim for a tide of 0.0 ft or lower. The lowest tides of the year (king tides and minus tides) expose the most marine life. Plan to arrive 30–60 minutes before low tide so you can explore as the water recedes, and start heading back as it returns — the incoming tide can cut off access fast.",
    "sort_order": 55
  },
  {
    "id": "tides-faq-3",
    "page": "tides",
    "question": "What is a king tide?",
    "answer": "King tides are the highest predicted tides of the year, occurring in winter and summer when the sun, moon, and Earth align. In San Diego they can exceed 7 ft and cause coastal flooding, beach narrowing, and dramatic wave run-up. Photographers love them; property owners brace for them.",
    "sort_order": 56
  },
  {
    "id": "tides-faq-4",
    "page": "tides",
    "question": "Why are there two high tides per day?",
    "answer": "The moon's gravity pulls ocean water toward it, creating a high tide on the moon-facing side of Earth. Inertia creates a second high tide on the opposite side. Earth rotates through both bulges every 24 hours and 50 minutes, so each high tide arrives about 50 minutes later each day.",
    "sort_order": 57
  },
  {
    "id": "tides-faq-5",
    "page": "tides",
    "question": "How do tides affect surfing?",
    "answer": "Most San Diego reef breaks have a preferred tide window. Sunset Cliffs surfs best at medium tide; La Jolla Cove flattens at high tide; some sandbar breaks in Pacific Beach favor lower tides. Surf reports usually note the optimal tide range alongside swell data.",
    "sort_order": 58
  },
  {
    "id": "tides-faq-6",
    "page": "tides",
    "question": "Can the tide be dangerous?",
    "answer": "Yes. A rapidly incoming tide can trap explorers below sea cliffs at Sunset Cliffs, Sandstone Bluffs, and Black's Beach. Always know when the next high tide arrives, and never explore caves or coves on a rising tide without an escape route above the high-tide line.",
    "sort_order": 59
  },
  {
    "id": "weather-faq-1",
    "page": "weather",
    "question": "What is the best month to visit San Diego for weather?",
    "answer": "September and October are widely considered the best — warm but not hot (75–80°F), water temperatures at their annual peak (around 70°F), reliably clear skies, and thinner crowds after Labor Day. May and June can be greyer than visitors expect due to the marine layer.",
    "sort_order": 66
  },
  {
    "id": "weather-faq-2",
    "page": "weather",
    "question": "What is 'May Gray' and 'June Gloom'?",
    "answer": "A persistent low marine layer of clouds that often blankets the coast through morning and sometimes all day in late spring. Caused by warm inland air sitting over cool ocean water. It usually burns off by early afternoon — but not always. Inland areas (15+ miles from the coast) often stay sunny.",
    "sort_order": 67
  },
  {
    "id": "weather-faq-3",
    "page": "weather",
    "question": "Does it ever rain in San Diego?",
    "answer": "Yes, mostly between November and March. Annual rainfall averages just 10 inches — about a quarter of New York City. Rain comes in concentrated bursts; the city can go six months between meaningful storms. When it does rain, expect surf runoff warnings for 72 hours.",
    "sort_order": 68
  },
  {
    "id": "weather-faq-4",
    "page": "weather",
    "question": "How hot does San Diego get in summer?",
    "answer": "Coastal areas average 72–78°F highs in July and August, rarely topping 85°F. Inland (El Cajon, Escondido) routinely hits 90–100°F. The famous moderate climate is a coastal phenomenon — drive 20 minutes inland and the temperature can jump 15°F.",
    "sort_order": 69
  },
  {
    "id": "weather-faq-5",
    "page": "weather",
    "question": "What is a Santa Ana wind?",
    "answer": "Strong, hot, dry winds that flow from the inland deserts toward the coast, usually in fall. They reverse the normal sea breeze, spike fire danger, and can push temperatures to record highs (90°F+ at the beach). They typically last 2–4 days at a time.",
    "sort_order": 70
  },
  {
    "id": "weather-faq-6",
    "page": "weather",
    "question": "Do I need a jacket in San Diego?",
    "answer": "Yes — even in summer. Evenings cool to the high 50s or low 60s on the coast, and the breeze adds chill. Pack a light layer year-round for sunset walks, restaurant patios, and boat trips. Inland evenings are warmer in summer but colder in winter.",
    "sort_order": 71
  },
  {
    "id": "activities-faq-1",
    "page": "activities",
    "question": "What's the best ocean adventure for first-timers?",
    "answer": "A guided kayak tour of the La Jolla sea caves is hard to beat — calm water, no experience required, sea lions and leopard sharks underfoot, and a 90-minute experience that makes everyone smile. Most tours include the kayak, paddle, life vest, and a guide.",
    "sort_order": 78
  },
  {
    "id": "activities-faq-2",
    "page": "activities",
    "question": "When is whale watching season in San Diego?",
    "answer": "Two seasons. Gray whales migrate past San Diego from December through April. Blue whales — the largest animals on Earth — appear from May through September. Year-round you can also see dolphin pods, fin whales, and the occasional orca. Three-hour boat tours from Mission Bay are the most common option.",
    "sort_order": 79
  },
  {
    "id": "activities-faq-3",
    "page": "activities",
    "question": "Can I snorkel in San Diego without a guide?",
    "answer": "Yes. La Jolla Cove and La Jolla Shores are the city's best snorkeling and free to access. Bring or rent your own gear, enter from the sand, and follow posted advisories. Visibility is best in summer (15–30 ft); cooler months can drop to 5–10 ft.",
    "sort_order": 80
  },
  {
    "id": "activities-faq-4",
    "page": "activities",
    "question": "Are leopard sharks dangerous?",
    "answer": "No. The leopard sharks that gather at La Jolla Shores in summer are bottom-feeders that eat small invertebrates. They are non-aggressive and have never been recorded biting a human in San Diego. Wading among them in waist-deep water is a signature local experience.",
    "sort_order": 81
  },
  {
    "id": "activities-faq-5",
    "page": "activities",
    "question": "Where can I learn to surf?",
    "answer": "Pacific Beach (Tourmaline Surfing Park) and La Jolla Shores both have surf schools that operate year-round. A 2-hour group lesson with board, wetsuit, and instruction runs about $80–120. Most students stand up on a foam longboard during the first lesson.",
    "sort_order": 82
  },
  {
    "id": "activities-faq-6",
    "page": "activities",
    "question": "Do I need to book adventures in advance?",
    "answer": "For weekends, yes — kayak tours, whale watching boats, sailing charters, and surf lessons all book full 1–2 weeks ahead in summer. Weekday spots are easier to grab. Many operators offer same-day discounts if afternoon slots are unfilled.",
    "sort_order": 83
  },
  {
    "id": "news-faq-1",
    "page": "news",
    "question": "Where does San Diego coastal news come from?",
    "answer": "We aggregate updates from local outlets (San Diego Union-Tribune, KPBS, Voice of San Diego, NBC 7 SD), the City of San Diego, NOAA, the California Coastal Commission, lifeguard advisories, and county Beach & Bay Status. Each story links to the original source.",
    "sort_order": 90
  },
  {
    "id": "news-faq-2",
    "page": "news",
    "question": "How often is the news updated?",
    "answer": "The page refreshes regularly through the day. Breaking advisories (water-quality warnings, pier closures, surf advisories) appear within hours. Feature stories (conservation projects, infrastructure) are added as they publish.",
    "sort_order": 91
  },
  {
    "id": "news-faq-3",
    "page": "news",
    "question": "Are water-quality warnings serious?",
    "answer": "Yes. The San Diego County Department of Environmental Health issues warnings when bacteria levels exceed safe limits — usually after rain runoff or sewage spills. Avoid swimming, surfing, or wading in posted areas. Warnings typically last 72 hours after rain.",
    "sort_order": 92
  },
  {
    "id": "news-faq-4",
    "page": "news",
    "question": "Why are some San Diego beaches frequently closed?",
    "answer": "Imperial Beach and the South Bay shoreline are routinely impacted by cross-border sewage flows from Tijuana. The federal government and Mexico are working on long-term infrastructure fixes. Until then, frequent advisories are normal in the southernmost stretches.",
    "sort_order": 93
  },
  {
    "id": "news-faq-5",
    "page": "news",
    "question": "Where can I report coastal pollution?",
    "answer": "The San Diego County Department of Environmental Health hotline (858-694-3886) handles water-quality complaints. For visible spills, call the U.S. Coast Guard National Response Center (800-424-8802). Plastic and trash issues can be reported to Surfrider Foundation San Diego.",
    "sort_order": 94
  },
  {
    "id": "news-faq-6",
    "page": "news",
    "question": "How do I follow San Diego conservation efforts?",
    "answer": "Surfrider San Diego, San Diego Coastkeeper, and the Cabrillo National Monument Foundation all run year-round volunteer cleanups and advocacy. Their newsletters and Instagram accounts post the most current opportunities and policy news.",
    "sort_order": 95
  },
  {
    "id": "events-faq-1",
    "page": "events",
    "question": "What's the biggest event in San Diego each year?",
    "answer": "Comic-Con International (mid-July) is the city's signature event, drawing 130,000+ attendees and transforming downtown for a week. Other annual standouts include the San Diego County Fair (June), the U.S. Open Sandcastle Competition (Imperial Beach), and the Holiday Bowl parade (December).",
    "sort_order": 102
  },
  {
    "id": "events-faq-2",
    "page": "events",
    "question": "When does San Diego have its best festivals?",
    "answer": "Summer (June–August) is peak festival season — outdoor concerts, food fests, the County Fair, July 4 fireworks at multiple beaches, and a packed weekend calendar. Spring brings ArtWalk, Earth Day events, and the Carlsbad Flower Fields. Fall offers Oktoberfests and surf contests.",
    "sort_order": 103
  },
  {
    "id": "events-faq-3",
    "page": "events",
    "question": "Are there free events to attend?",
    "answer": "Many — Concerts in the Park (Balboa Park, summer Sundays), the December Balboa Park December Nights, First Friday art walks in various neighborhoods, weekly farmers markets in every neighborhood, and most beach bonfire events at Mission Beach are free.",
    "sort_order": 104
  },
  {
    "id": "events-faq-4",
    "page": "events",
    "question": "How early should I book lodging during big events?",
    "answer": "For Comic-Con, book hotels 6–12 months ahead and expect rates 3–5x normal. For Rock'n'Roll Marathon, Holiday Bowl, and the County Fair finals weekend, 2–3 months ahead is reasonable. Most other events don't significantly impact lodging.",
    "sort_order": 105
  },
  {
    "id": "events-faq-5",
    "page": "events",
    "question": "Where can I find a current event calendar?",
    "answer": "The City of San Diego, Visit San Diego, San Diego Magazine, and the U-T's events page all maintain calendars. Locally, neighborhood Instagram accounts (@northparkmainstreet, @lajollatoday) post weekly happenings. The events listed on this page are curated and updated.",
    "sort_order": 106
  },
  {
    "id": "events-faq-6",
    "page": "events",
    "question": "Do I need tickets for July 4 fireworks?",
    "answer": "No — the major shows at Mission Bay (SeaWorld), Big Bay Boom (over San Diego Bay), and Coronado are free to view from public beaches and parks. Premium reserved seating is available at some venues. Arrive 2–3 hours early for parking and a good viewing spot.",
    "sort_order": 107
  }
];
